/**
 * Which rules are allowed to judge which elements.
 * For Media Devoted | Created By Ciro Autiero
 *
 * An element the tracker has no numbers for is deliberately left OUT of scoring
 * — a 0 would punish it for being new — and that same exclusion kept it out of
 * tagging, so no rule a user could write was able to reach it. This is the seam
 * that lets a rule opt IN to exactly that population.
 *
 * `isEligible` is deliberately NOT touched. It has two callers with different
 * meanings — `createRankIndex` ("who is in the ranking pool") and the tagging
 * loop ("who is considered for tagging"). Widening it would satisfy the second
 * and silently break the first, putting a scoreless element into the pool where
 * it would rank last and hand itself to every "score is lowest" rule.
 */

import { isEligible } from "./ranking";
import type { FlowTagItem, FlowTagRule } from "./types";

export function isNoStatsRule(rule: FlowTagRule): boolean {
  return rule.appliesTo === "no-stats";
}

/**
 * A no-stats element that is otherwise HEALTHY. Never for inactive, never for
 * rule-excluded: a PAUSED element with no stats is paused first, and tagging it
 * "new" would read as an invitation to leave it running.
 */
export function isNoStatsItem(item: FlowTagItem): boolean {
  return (
    item.noMetrics === true && item.active !== false && !item.ruleExcluded
  );
}

/**
 * A PARTITION, not a filter. The noMetrics arm is checked FIRST and the two
 * arms return disjoint rule sets, so no element is ever judged by both — which
 * makes double-tagging structurally impossible rather than merely unlikely.
 */
export function rulesForItem(
  item: FlowTagItem,
  rules: ReadonlyArray<FlowTagRule>,
): FlowTagRule[] {
  const mine = rules.filter((r) => r.layer === item.layer);
  if (item.noMetrics === true) {
    return isNoStatsItem(item) ? mine.filter(isNoStatsRule) : [];
  }
  return isEligible(item) ? mine.filter((r) => !isNoStatsRule(r)) : [];
}
